import {
  MOON_600,
  RED_600,
  SIENNA_600,
} from '@wandb/weave/common/css/globals.styles';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {Button} from '@wandb/weave/components/Button';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {PanelLink} from '@wandb/weave/components/Panel2/PanelLink';
import {IconOnlyPill} from '@wandb/weave/components/Tag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {TooltipDeprecated} from '@wandb/weave/components/TooltipDeprecated';
import {
  constFunction,
  constNumber,
  constString,
  constTimestamp,
  opArray,
  opArtifactAliasAlias,
  opArtifactAliases,
  opArtifactId,
  opArtifactIsPortfolio,
  opArtifactMembershipArtifactAliases,
  opArtifactMembershipArtifactVersion,
  opArtifactMembershipCollection,
  opArtifactMemberships,
  opArtifactMembershipVersionIndex,
  opArtifactName,
  opArtifactProject,
  opArtifactType,
  opArtifactTypeName,
  opArtifactVersionArtifactSequence,
  opArtifactVersionArtifactType,
  opArtifactVersionCollections,
  opArtifactVersionCreatedAt,
  opArtifactVersionCreatedBy,
  opArtifactVersionCreatedByUser,
  opArtifactVersionDescription,
  opArtifactVersionDigest,
  opArtifactVersionFileCount,
  opArtifactVersionId,
  opArtifactVersionIsGenerated,
  opArtifactVersionIsLinkedToGlobalRegistry,
  opArtifactVersionLink,
  opArtifactVersionMemberships,
  opArtifactVersionName,
  opArtifactVersionSize,
  opArtifactVersionTTLDurationSeconds,
  opArtifactVersionTTLIsInherited,
  opArtifactVersionUsedBy,
  opCount,
  opDatetimeAddMs,
  opDict,
  opEntityArtifactTTLDurationSeconds,
  opEntityName,
  opFilter,
  opIsNone,
  opMap,
  opNoneCoalesce,
  opNumberMult,
  opProjectEntity,
  opProjectName,
  opRunLink,
  opRunUsedArtifactVersions,
  opStringReplace,
  opTimestampMax,
  opTimestampRelativeStringAutoFormat,
  opUserLink,
  OutputNode,
} from '@wandb/weave/core';
import {useEach, useNodeValue, useRefreshAllNodes} from '@wandb/weave/react';
import _ from 'lodash';
import numeral from 'numeral';
import * as React from 'react';
import {useCallback, useMemo, useState} from 'react';
import {Link, useHistory} from 'react-router-dom';
import {toast} from 'react-toastify';

import CopyableText from '../../../../components/CopyableText';
import {
  PlanType,
  useEntityOrganizationQuery,
  useOrganizationSubscriptionsBasicDetailsQuery,
  useUpdateArtifactMutation,
} from '../../../../generated/graphql';
import {useEntityPageData} from '../../../../pages/EntityPage/useEntityPageData';
import {useViewer} from '../../../../state/viewer/hooks';
import {isCommunityEditionPrimarySubscription} from '../../../../util/accounts/pricing';
import {useAdminModeActive} from '../../../../util/admin';
import {
  addSeconds,
  DateFormat,
  differenceInDays,
  format,
} from '../../../../util/date';
import DOC_URLS from '../../../../util/doc_urls';
import {useEntitySettings} from '../../../../util/entitySettings';
import {propagateErrorsContext} from '../../../../util/errors';
import {trackBannerButtonClicked} from '../../../../util/navigation';
import * as urls from '../../../../util/urls';
import {
  BannerBoldText,
  BannerButton,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../../Banner';
import {TeamMemberRole} from '../../../MembersTable/TeamMembersTable/utils';
import {
  useIsGlobalRegistryEnabled,
  useIsProjectReadOnly,
} from '../../../Registries/common/hooks';
import {
  checkRegistryProject,
  resolveRegistryUrl,
} from '../../../Registries/common/utils';
import {EditTTLDrawer} from '../../../Registries/workflows/CreateRegisteredCollectionDrawer/ArtifactVersionTTLDrawer/EditArtifactTTLDrawer';
import {RemoveArtifactTTLModal} from '../../../Registries/workflows/CreateRegisteredCollectionDrawer/ArtifactVersionTTLDrawer/RemoveArtifactTTLModal';
import {Artifact} from '../../../Registries/workflows/CreateRegisteredCollectionDrawer/CreateRegisteredCollectionDrawerInner';
import {LinkArtifactDrawer} from '../../../Registries/workflows/CreateRegisteredCollectionDrawer/LinkArtifactDrawer/LinkArtifactDrawer';
import {PanelArtifactMembershipOverviewAliasList} from '../PanelArtifactMembershipOverviewAliasList';
import {PanelArtifactMembershipOverviewTagList} from '../PanelArtifactMembershipOverviewTagList';
import {InstrumentedCenteredWaveLoader as Loader} from './../../../../components/utility/InstrumentedLoader';
import {DeleteMembershipModal} from './DeleteMembershipModal';
import {OverviewItem} from './OverviewItem';
import * as S from './style';

export type ConfigType = {};
const inputType = 'artifactMembership' as const;
export const PanelArtifactMembershipOverview: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType> & {
    updateSelectedMembershipIdentifier?: (identifier: string) => void;
    artifactRegistry?: boolean;
  }
> = props => {
  const artifactMembershipNode = props.input;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [linkIsOpen, setLinkIsOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [updateArtifact] = useUpdateArtifactMutation({
    context: propagateErrorsContext(),
  });
  const refreshAll = useRefreshAllNodes();
  const history = useHistory();
  const [isRemoveTTLOpen, setIsRemoveTTLOpen] = useState(false);
  const [isEditTTLOpen, setIsEditTTLOpen] = useState(false);
  const artifactVersionNode = useMemo(
    () =>
      opArtifactMembershipArtifactVersion({
        artifactMembership: artifactMembershipNode,
      }),
    [artifactMembershipNode]
  );
  // With org-level registries allowing access to team-level artifacts,
  // there are cases where users can have access to the artifact,
  // but not its source run or project/team. This access check on the frontend
  // will prevent us from making queries that we know will crash for such users.
  const noProjectAccess = useNodeValue(
    useMemo(
      () =>
        opIsNone({
          val: opArtifactProject({
            artifact: opArtifactVersionArtifactSequence({
              artifactVersion: artifactVersionNode,
            }),
          }),
        }),
      [artifactVersionNode]
    )
  );
  const artifactNode = useMemo(
    () =>
      opArtifactMembershipCollection({
        artifactMembership: artifactMembershipNode,
      }),
    [artifactMembershipNode]
  );
  const portfolioMembershipsNode = useMemo(() => {
    return opFilter({
      arr: opArtifactVersionMemberships({
        artifactVersion: artifactVersionNode,
      }),
      filterFn: constFunction({row: 'artifactMembership'}, ({row}) => {
        return opArtifactIsPortfolio({
          artifact: opArtifactMembershipCollection({
            artifactMembership: row,
          }),
        });
      }),
    });
  }, [artifactVersionNode]);

  const artifactVersionLinkNode = useMemo(
    () =>
      opArtifactVersionLink({
        artifactVersion: artifactVersionNode,
      }) as any,
    [artifactVersionNode]
  );

  const createdByLinkNode = useMemo(
    () =>
      opNoneCoalesce({
        lhs: opRunLink({
          run: opArtifactVersionCreatedBy({
            artifactVersion: artifactVersionNode,
          }),
        }),
        rhs: opUserLink({
          user: opArtifactVersionCreatedByUser({
            artifactVersion: artifactVersionNode,
          }),
        }),
      }) as any,
    [artifactVersionNode]
  );

  const {result, loading} = useNodeValue(
    useMemo(
      () =>
        getDataDict(
          artifactNode as any,
          artifactVersionNode as any,
          artifactMembershipNode as any
        ),
      [artifactMembershipNode, artifactNode, artifactVersionNode]
    )
  );
  const portfolioMembershipNodes = useEach(portfolioMembershipsNode as any);

  const {entitySettings} = useEntitySettings(result ? result.entityName : ``);
  const entityPageData = useEntityPageData(result ? result?.entityName : '');
  const viewer = useViewer();

  const {isProjectReadOnly: readOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(result?.entityName ?? '', result?.projectName ?? '');
  const {data: entityOrganizationData} = useEntityOrganizationQuery({
    variables: {teamName: result?.entityName as string},
    skip: result?.entityName == null,
  });

  const orgID = entityOrganizationData?.entity?.organization?.id || '';
  const orgName = entityOrganizationData?.entity?.organization?.name || '';

  const {data: organizationData} =
    useOrganizationSubscriptionsBasicDetailsQuery({
      variables: {organizationId: orgID},
      skip: entityOrganizationData?.entity?.organization?.id == null,
    });

  const primarySub = organizationData?.organization?.subscriptions?.find(
    s => s.plan.planType === PlanType.Primary
  );

  const isPrimarySubCommunityEdition =
    primarySub != null && isCommunityEditionPrimarySubscription(primarySub);

  const isAdminModeActive = useAdminModeActive();
  const viewerIsTeamAdmin = entityPageData.data?.members.some(
    m => m.username === viewer?.username && m.role === TeamMemberRole.ADMIN
  );

  const viewerIsAdmin = isAdminModeActive || viewerIsTeamAdmin;

  const artifactHasCustomTTL = !result?.ttlIsInherited;
  const userCanEditTTL =
    (!entitySettings?.disableMembersEditArtifactTTL || viewerIsAdmin) &&
    !result?.artifactIsGenerated;

  const hasAliases = _.some(
    result?.artifactMembershipsMaterialized ?? [],
    m => m.aliases.length > 0
  );

  const expiryTimestamp =
    result?.ttlDurationSeconds && result?.ttlDurationSeconds > 0
      ? addSeconds(
          result.createdAt != null ? new Date(result.createdAt) : new Date(),
          result?.ttlDurationSeconds
        )
      : null;

  const ttlDaysLeft = expiryTimestamp
    ? differenceInDays(expiryTimestamp, new Date())
    : null;

  const ttlStatus =
    ttlDaysLeft == null || result?.artifactIsGenerated
      ? null
      : ttlDaysLeft > 30
      ? 'info'
      : ttlDaysLeft > 7
      ? 'warning'
      : 'error';

  const afterLink = useCallback(
    res => {
      setLinkIsOpen(false);
      const {
        registryName: linkedRegistryName,
        isRegistryProject: isLinkedRegistryProject,
      } = checkRegistryProject(res.projectName);
      refreshAll().then(() =>
        toast.success(
          <S.SuccessfulLink>
            <Icon
              name="checkmark-circle"
              width={24}
              height={24}
              style={{alignSelf: 'flex-start'}}
            />
            <S.SuccessfulLinkMessage>
              New version added to collection <b>'{res.collectionName}'</b>.{' '}
              <S.ClickableText>
                <TargetBlank
                  href={resolveRegistryUrl(
                    isLinkedRegistryProject,
                    orgName,
                    linkedRegistryName ?? 'model',
                    {
                      entityName: res.entityName,
                      projectName: res.projectName,
                      collectionName: res.collectionName,
                    },
                    res.entityName
                  )}>
                  View in registry
                </TargetBlank>
              </S.ClickableText>
            </S.SuccessfulLinkMessage>
          </S.SuccessfulLink>,
          {
            position: 'bottom-right',
          }
        )
      );
    },
    [refreshAll, orgName]
  );
  const {enableGlobalRegistry, enableGlobalRegistryLoading} =
    useIsGlobalRegistryEnabled(result?.entityName, loading);

  if (
    loading ||
    portfolioMembershipNodes.loading ||
    enableGlobalRegistryLoading ||
    noProjectAccess.loading ||
    isProjectReadOnlyLoading
  ) {
    return <Loader name="artifact-membership-overview" size="huge" />;
  }
  const {registryName, isRegistryProject} = checkRegistryProject(
    result.projectName
  );

  // For LinkArtifactDrawer
  const linkArtifact: Artifact = {
    entityName: result.entityName,
    projectName: result.projectName,
    sequenceName: result.artifactName,
    alias: '', // Not needed for linking
    id: result.artifactVersionId,
    versionIndex: result.versionIndex,
    ttlDurationSeconds: result.ttlDurationSeconds,
    createdAt: result.createdAt,
  };
  const showLinkToRegistryButton =
    readOnly || result.artifactIsPortfolio || result.artifactIsGenerated
      ? false
      : enableGlobalRegistry
      ? true
      : result.artifactTypeName.toLowerCase().includes('model');
  return (
    <S.OverviewWrapper>
      <S.OverviewPanel>
        {/* Membership  */}
        <S.OverviewPanelSection>
          {ttlStatus && ttlStatus !== 'info' && (
            <S.BannerWithMarginVersion variant={ttlStatus}>
              <BannerFlexWrapper>
                <Icon
                  name={
                    ttlStatus && ttlStatus === 'error' ? 'warning-alt' : 'info'
                  }
                  style={{marginRight: '5px'}}
                />
                <BannerTextWrapper>
                  {<BannerBoldText>Version expiring soon.</BannerBoldText>} This
                  version{' '}
                  {result?.ttlDiffString === 'Pending deletion'
                    ? 'is pending deletion'
                    : 'will be deleted in ' + result?.ttlDiffString}{' '}
                  ({'Expiry: '}
                  {expiryTimestamp != null &&
                    format(
                      expiryTimestamp,
                      DateFormat.MONTH_DAYTH_YEAR_24HOUR_MIN_SECONDS
                    )}
                  )
                </BannerTextWrapper>
              </BannerFlexWrapper>

              <BannerButton
                variant={ttlStatus}
                text="Learn more"
                link={DOC_URLS.artifactTTL}
                eventData={{
                  buttonInfo: 'Artifact TTL Version Banner Button Clicked',
                  entityName: result?.entityName,
                  projectName: result?.projectName,
                  artifactID: result?.artifactId,
                  artifactTTLDuration: result?.ttlDurationSeconds,
                }}
              />
            </S.BannerWithMarginVersion>
          )}
          <S.OverviewPanelSectionHeader>
            <S.OverviewPanelSectionHeaderTitle>
              Version overview
            </S.OverviewPanelSectionHeaderTitle>
            {!readOnly && (
              <S.TriggerContainer data-test="membership-options-delete">
                {result.artifactIsPortfolio && (
                  <Button
                    size="medium"
                    icon="delete"
                    variant="secondary"
                    onClick={() => {
                      setIsDeleteModalOpen(true);
                    }}>
                    Unlink version
                  </Button>
                )}
                {showLinkToRegistryButton && (
                  <Button
                    data-test="link-artifact"
                    icon="link-alt"
                    size="medium"
                    variant="primary"
                    style={{marginLeft: '8px'}}
                    onClick={() => setLinkIsOpen(true)}>
                    Link to registry
                  </Button>
                )}

                {!result.artifactIsPortfolio && (
                  <Tailwind>
                    <div
                      className={`ml-8 inline-block ${
                        isPrimarySubCommunityEdition ? 'mr-20' : ''
                      }`}>
                      {!readOnly && !result.artifactIsGenerated && (
                        <DropdownMenu.Root
                          open={isMenuOpen}
                          onOpenChange={setIsMenuOpen}>
                          <DropdownMenu.Trigger>
                            <Button
                              className="artifacts-menu-button"
                              size="medium"
                              variant="ghost"
                              icon="overflow-horizontal"
                              active={isMenuOpen}
                            />
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Portal>
                            <DropdownMenu.Content align="end">
                              <DropdownMenu.Group>
                                {showLinkToRegistryButton && (
                                  <DropdownMenu.Item
                                    onClick={() => setLinkIsOpen(true)}>
                                    <Icon name="add-new" />
                                    Link new version
                                  </DropdownMenu.Item>
                                )}
                                {userCanEditTTL && (
                                  <DropdownMenu.Item
                                    onClick={
                                      !isPrimarySubCommunityEdition
                                        ? () => setIsEditTTLOpen(true)
                                        : undefined
                                    }>
                                    <Icon name="recent-clock" />
                                    Edit TTL policy
                                    {isPrimarySubCommunityEdition && (
                                      <TooltipDeprecated
                                        trigger={
                                          <div>
                                            <IconOnlyPill
                                              color="purple"
                                              icon="crown-pro"
                                            />
                                          </div>
                                        }
                                        content="Upgrade in order to access this feature"
                                        position="top right"
                                      />
                                    )}
                                  </DropdownMenu.Item>
                                )}
                                {userCanEditTTL && artifactHasCustomTTL && (
                                  <DropdownMenu.Item
                                    onClick={() => setIsRemoveTTLOpen(true)}>
                                    <Icon name="remove-alt" />
                                    Remove TTL policy
                                  </DropdownMenu.Item>
                                )}
                              </DropdownMenu.Group>
                              <DropdownMenu.Separator />
                              <DropdownMenu.Item
                                onClick={() => setIsDeleteModalOpen(true)}>
                                <Icon name="delete" />
                                Delete Version
                              </DropdownMenu.Item>
                            </DropdownMenu.Content>
                          </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                      )}
                    </div>
                  </Tailwind>
                )}
                <DeleteMembershipModal
                  open={isDeleteModalOpen}
                  setIsOpen={setIsDeleteModalOpen}
                  onClose={() => setIsDeleteModalOpen(false)}
                  isPortfolio={result.artifactIsPortfolio}
                  artifactName={result.artifactName}
                  versionIndex={result.versionIndex}
                  hasAliases={hasAliases}
                  hasMemberships={
                    result.artifactMembershipsMaterialized.length > 0
                  }
                  afterDelete={() => {
                    let url = '';
                    if (props.artifactRegistry) {
                      // Since portfolios can exist both in and outside the Model
                      // Registry, post unlinking, the user is redirected to the
                      // portfolio view within the same interface that they began
                      url = resolveRegistryUrl(
                        isRegistryProject,
                        orgName,
                        registryName ?? 'model',
                        {
                          entityName: result.entityName,
                          projectName: result.projectName,
                          collectionName: result.artifactName,
                        },
                        result.entityName,
                        'membership',
                        undefined,
                        'overview'
                      );
                    } else {
                      url = urls.artifactSequence({
                        entityName: result.entityName,
                        projectName: result.projectName,
                        artifactTypeName: result.artifactTypeName,
                        artifactSequenceName: result.artifactName,
                      });
                    }
                    refreshAll().then(() => history.push(url));
                  }}
                  artifactId={result.artifactId}
                  artifactVersionId={result.artifactVersionId}
                  artifactIsLinkedToGlobalRegistry={
                    result.artifactIsLinkedToGlobalRegistry
                  }
                />
              </S.TriggerContainer>
            )}
          </S.OverviewPanelSectionHeader>
          <S.OverviewPanelSectionBody>
            <S.OverviewPanelSectionBodyRow>
              <S.OverviewPanelSectionBodyColumn>
                <OverviewItem
                  label={'Full Name'}
                  value={
                    <CopyableText
                      text={`${result.entityName}/${result.projectName}/${result.artifactName}:v${result.versionIndex}`}
                      toastText={'Copied version to clipboard'}
                    />
                  }
                />
                <OverviewItem
                  label={'Aliases'}
                  value={
                    <S.OverviewPanelTagOrAliasContainer>
                      <PanelArtifactMembershipOverviewAliasList
                        {...Panel2.dummyProps}
                        input={artifactMembershipNode}
                      />
                    </S.OverviewPanelTagOrAliasContainer>
                  }
                />
                <OverviewItem
                  label={'Tags'}
                  value={
                    <S.OverviewPanelTagOrAliasContainer>
                      <PanelArtifactMembershipOverviewTagList
                        {...Panel2.dummyProps}
                        input={artifactMembershipNode}
                        // Note: It's easier to get project/entity from artifactMembershipNode than artifactVersionNode
                        showButton={true}
                        wrap={true}
                        showColor={true}
                        tagSize={'large'}
                      />
                    </S.OverviewPanelTagOrAliasContainer>
                  }
                />
                <OverviewItem label={'Digest'} value={result.digest} />
                {result.artifactIsPortfolio ? (
                  <OverviewItem
                    label={'Source Version'}
                    value={
                      noProjectAccess.result && props.artifactRegistry ? (
                        'REDACTED'
                      ) : (
                        <PanelLink
                          {...Panel2.dummyProps}
                          input={artifactVersionLinkNode}
                        />
                      )
                    }
                  />
                ) : (
                  result.portfolioMembershipsMaterialized?.length > 0 && (
                    <OverviewItem
                      label={'Linked To'}
                      value={
                        <>
                          {result.portfolioMembershipsMaterialized.map(
                            (node: any, ndx: number) => {
                              const {
                                registryName: nodeRegistryName,
                                isRegistryProject: isNodeInRegistryProject,
                              } = checkRegistryProject(node.projectName);
                              return (
                                <div key={ndx}>
                                  <Link
                                    to={resolveRegistryUrl(
                                      isNodeInRegistryProject,
                                      orgName,
                                      nodeRegistryName ?? 'model',
                                      {
                                        entityName: node.entityName,
                                        projectName: node.projectName,
                                        collectionName: node.collectionName,
                                      },
                                      result.entityName,
                                      undefined,
                                      `v${node.versionIndex}`,
                                      'overview'
                                    )}>{`${node.collectionName}:v${node.versionIndex}`}</Link>
                                </div>
                              );
                            }
                          )}
                        </>
                      }
                    />
                  )
                )}
                <OverviewItem
                  label={'Created By'}
                  value={
                    noProjectAccess.result && props.artifactRegistry ? (
                      'REDACTED'
                    ) : (
                      <PanelLink
                        {...Panel2.dummyProps}
                        input={createdByLinkNode}
                      />
                    )
                  }
                />
              </S.OverviewPanelSectionBodyColumn>
              <S.OverviewPanelSectionBodyColumn>
                <OverviewItem
                  label="Created At"
                  value={format(
                    result.createdAt ? new Date(result.createdAt) : new Date(),
                    DateFormat.MONTH_DAYTH_YEAR_24HOUR_MIN_SECONDS,
                    'UTC'
                  )}
                />
                <OverviewItem
                  label={'Num Consumers'}
                  value={result.numConsumers}
                />
                <OverviewItem label={'Num Files'} value={result.numFiles} />
                <OverviewItem
                  label={'Size'}
                  value={numeral(result.artifactSize).format('0.0b')}
                />
                {!result?.artifactIsGenerated && (
                  <OverviewItem
                    label={'TTL Remaining'}
                    labelStyle={
                      <TooltipDeprecated
                        trigger={
                          <Icon
                            name="info"
                            style={{
                              cursor: 'pointer',
                              verticalAlign: 'middle',
                              marginLeft: '4px',
                            }}
                          />
                        }
                        content={
                          <span>
                            TTL allows users to set an expiration period,
                            <br />
                            after which the version will be deleted.
                            <br />
                            <S.StyledLink
                              onClick={() => {
                                trackBannerButtonClicked({
                                  buttonInfo:
                                    'Artifact TTL Version How To Modify Clicked',
                                  entityName: result?.entityName,
                                  artifactID: result?.artifactId,
                                  artifactTTLDuration:
                                    result?.ttlDurationSeconds,
                                });
                              }}
                              href={DOC_URLS.artifactTTL}
                              target="_blank"
                              rel="noopener noreferrer">
                              {' '}
                              How to modify TTL{' '}
                            </S.StyledLink>
                          </span>
                        }
                        position={'bottom right'}
                        inverted
                        size="tiny"
                        on="click"
                        wide={true}
                      />
                    }
                    value={
                      ttlStatus != null ? (
                        <div
                          style={
                            ttlStatus === 'error'
                              ? {color: RED_600}
                              : ttlStatus === 'warning'
                              ? {color: SIENNA_600}
                              : {color: MOON_600}
                          }>
                          {expiryTimestamp != null && (
                            <TooltipDeprecated
                              trigger={<span>{result?.ttlDiffString}</span>}
                              content={
                                'This version will be deleted on ' +
                                format(
                                  expiryTimestamp,
                                  DateFormat.MONTH_DAYTH_YEAR_24HOUR_MIN_SECONDS
                                )
                              }
                              position={'bottom center'}
                              inverted
                              wide={'very'}
                            />
                          )}
                        </div>
                      ) : (
                        'Inactive'
                      )
                    }
                  />
                )}
                {result.peerArtifacts != null &&
                  result.peerArtifacts.length > 0 && (
                    <OverviewItem
                      label={'Upstream Artifacts'}
                      value={result.peerArtifacts.map((p, i) => {
                        const [artifactCollectionName, artifactCommitHash] =
                          p.versionName.split(':');
                        return (
                          <Link
                            key={`${i}-${artifactCommitHash}`}
                            to={urls.artifact({
                              entityName: p.entityName,
                              projectName: p.projectName,
                              artifactTypeName: p.type,
                              artifactCollectionName,
                              artifactCommitHash,
                            })}>
                            {p.versionName}
                          </Link>
                        );
                      })}
                    />
                  )}
              </S.OverviewPanelSectionBodyColumn>
            </S.OverviewPanelSectionBodyRow>
            <S.OverviewPanelSectionBodyRow>
              <OverviewItem
                label={'Description'}
                value={
                  <S.OverviewValueMarkdownEditor
                    readOnly={readOnly}
                    saveText={false}
                    placeholder={'What changed in this version?'}
                    serverText={result?.versionDescription}
                    onChange={value => {
                      updateArtifact({
                        variables: {
                          artifactID: result.artifactVersionId,
                          description: value,
                        },
                      }).catch(err => {
                        console.error(err);
                        toast(
                          'Something went wrong while trying to change the description.'
                        );
                      });
                    }}
                  />
                }
              />
            </S.OverviewPanelSectionBodyRow>
          </S.OverviewPanelSectionBody>
        </S.OverviewPanelSection>
      </S.OverviewPanel>
      <LinkArtifactDrawer
        open={linkIsOpen}
        closeDrawer={() => setLinkIsOpen(false)}
        artifactTypeName={result.artifactTypeName}
        // remove the defaultProjectName prop after Model Registry is deprecated
        defaultProjectName={'model-registry'}
        artifact={linkArtifact}
        afterLink={afterLink}
      />
      <EditTTLDrawer
        open={isEditTTLOpen}
        closeDrawer={() => {
          setIsEditTTLOpen(false);
        }}
        artifact={linkArtifact}
        ttlIsInherited={result.ttlIsInherited}
        entityTTL={result.entityArtifactTTL}
      />
      <RemoveArtifactTTLModal
        open={isRemoveTTLOpen}
        onClose={() => {
          setIsRemoveTTLOpen(false);
        }}
        artifactName={result.artifactName}
        versionIndex={result.versionIndex}
        artifactID={result.artifactVersionId}
      />
    </S.OverviewWrapper>
  );
};

function getDataDict(
  artifactNode: OutputNode<'artifact'>,
  artifactVersionNode: OutputNode<'artifactVersion'>,
  artifactMembershipNode: OutputNode<'artifactMembership'>
) {
  const artifactTypeNode = opArtifactType({artifact: artifactNode});
  const artifactProjectNode = opArtifactProject({
    artifact: artifactNode,
  });
  const memberships = opFilter({
    arr: opArtifactVersionCollections({
      artifactVersion: artifactVersionNode,
    }),
    filterFn: constFunction({row: 'artifact'}, ({row}) => {
      return opArtifactIsPortfolio({artifact: row});
    }),
  });

  const portfolioMemberships = opFilter({
    arr: opArtifactVersionMemberships({
      artifactVersion: artifactVersionNode,
    }),
    filterFn: constFunction({row: 'artifactMembership'}, ({row}) => {
      return opArtifactIsPortfolio({
        artifact: opArtifactMembershipCollection({
          artifactMembership: row,
        }),
      });
    }),
  });

  const peerArtifactVersions = opRunUsedArtifactVersions({
    run: opArtifactVersionCreatedBy({
      artifactVersion: artifactVersionNode,
    }),
  });

  const currentTime = new Date().getTime();
  return opDict({
    artifactVersionId: opArtifactVersionId({
      artifactVersion: opArtifactMembershipArtifactVersion({
        artifactMembership: artifactMembershipNode,
      }),
    }),
    artifactId: opArtifactId({
      artifact: artifactNode,
    }),
    artifactAliases: opArtifactAliasAlias({
      artifactAlias: opArtifactAliases({
        artifact: artifactNode,
      }),
    }),
    artifactTypeName: opArtifactTypeName({
      artifactType: artifactTypeNode,
    }),
    artifactMembershipsMaterialized: opMap({
      arr: opArtifactMemberships({
        artifact: artifactNode,
      }),
      mapFn: constFunction({row: 'artifactMembership'}, ({row}) => {
        return opDict({
          versionIndex: opArtifactMembershipVersionIndex({
            artifactMembership: row,
          }),
          aliases: opArtifactAliasAlias({
            artifactAlias: opArtifactMembershipArtifactAliases({
              artifactMembership: row,
            }),
          }),
        } as any);
      }),
    }),
    portfolioMembershipsMaterialized: opMap({
      arr: portfolioMemberships,
      mapFn: constFunction({row: 'artifactMembership'}, ({row}) => {
        return opDict({
          versionIndex: opArtifactMembershipVersionIndex({
            artifactMembership: row,
          }),
          entityName: opEntityName({
            entity: opProjectEntity({
              project: opArtifactProject({
                artifact: opArtifactMembershipCollection({
                  artifactMembership: row,
                }),
              }),
            }),
          }),
          projectName: opProjectName({
            project: opArtifactProject({
              artifact: opArtifactMembershipCollection({
                artifactMembership: row,
              }),
            }),
          }),
          collectionName: opArtifactName({
            artifact: opArtifactMembershipCollection({
              artifactMembership: row,
            }),
          }),
        } as any);
      }),
    }),
    entityName: opEntityName({
      entity: opProjectEntity({
        project: artifactProjectNode,
      }),
    }),
    entityArtifactTTL: opEntityArtifactTTLDurationSeconds({
      entity: opProjectEntity({
        project: artifactProjectNode,
      }),
    }),
    projectName: opProjectName({
      project: artifactProjectNode,
    }),
    artifactName: opArtifactName({
      artifact: artifactNode,
    }),
    versionIndex: opArtifactMembershipVersionIndex({
      artifactMembership: artifactMembershipNode,
    }),
    versionDescription: opArtifactVersionDescription({
      artifactVersion: artifactVersionNode,
    }),
    digest: opArtifactVersionDigest({
      artifactVersion: artifactVersionNode,
    }),
    createdAt: opArtifactVersionCreatedAt({
      artifactVersion: artifactVersionNode,
    }),
    numConsumers: opCount({
      arr: opArtifactVersionUsedBy({
        artifactVersion: artifactVersionNode,
      }),
    }),
    numFiles: opArtifactVersionFileCount({
      artifactVersion: artifactVersionNode,
    }),
    artifactSize: opArtifactVersionSize({
      artifactVersion: artifactVersionNode,
    }),
    ttlDurationSeconds: opArtifactVersionTTLDurationSeconds({
      artifactVersion: artifactVersionNode,
    }),
    ttlIsInherited: opArtifactVersionTTLIsInherited({
      artifactVersion: artifactVersionNode,
    }),
    ttlDiffString: opStringReplace({
      str: opTimestampRelativeStringAutoFormat({
        lhs: opTimestampMax({
          timestamps: opArray({
            0: opDatetimeAddMs({
              lhs: opArtifactVersionCreatedAt({
                artifactVersion: artifactVersionNode,
              }),
              rhs: opNumberMult({
                lhs: opArtifactVersionTTLDurationSeconds({
                  artifactVersion: artifactVersionNode,
                }),
                rhs: constNumber(1000),
              }),
            }),
            1: constTimestamp(currentTime),
          } as any),
        }),
        rhs: constTimestamp(currentTime),
      }),
      sub: constString('less than 1 ms'),
      newSub: constString('Pending deletion'),
    }),
    artifactIsGenerated: opArtifactVersionIsGenerated({
      artifactVersion: artifactVersionNode,
    }),
    artifactIsLinkedToGlobalRegistry: opArtifactVersionIsLinkedToGlobalRegistry(
      {
        artifactVersion: artifactVersionNode,
      }
    ),
    artifactIsPortfolio: opArtifactIsPortfolio({
      artifact: artifactNode,
    }),
    portfolioMemberships: opMap({
      arr: memberships,
      mapFn: constFunction({row: 'artifact'}, ({row}) => {
        return opDict({
          name: opArtifactName({artifact: row}),
          typeName: opArtifactTypeName({
            artifactType: opArtifactType({artifact: row}),
          }),
          project: opProjectName({
            project: opArtifactProject({artifact: row}),
          }),
          entity: opEntityName({
            entity: opProjectEntity({
              project: opArtifactProject({artifact: row}),
            }),
          }),
        } as any);
      }),
    }),
    peerArtifacts: opMap({
      arr: peerArtifactVersions,
      mapFn: constFunction({row: 'artifactVersion'}, ({row}) => {
        return opDict({
          versionName: opArtifactVersionName({
            artifactVersion: row,
          }),
          type: opArtifactTypeName({
            artifactType: opArtifactVersionArtifactType({
              artifactVersion: row,
            }),
          }),
          entityName: opEntityName({
            entity: opProjectEntity({
              project: opArtifactProject({
                artifact: opArtifactVersionArtifactSequence({
                  artifactVersion: row,
                }),
              }),
            }),
          }),
          projectName: opProjectName({
            project: opArtifactProject({
              artifact: opArtifactVersionArtifactSequence({
                artifactVersion: row,
              }),
            }),
          }),
        } as any);
      }),
    }),
  } as any) as OutputNode<{
    type: 'typedDict';
    propertyTypes: {
      artifactId: 'string';
      artifactAliases: {type: 'list'; objectType: 'string'};
      artifactMembershipsMaterialized: {
        type: 'list';
        objectType: {
          type: 'typedDict';
          propertyTypes: {
            versionIndex: 'number';
            aliases: {type: 'list'; objectType: 'string'};
          };
        };
      };
      portfolioMembershipsMaterialized: {
        type: 'list';
        objectType: {
          type: 'typedDict';
          propertyTypes: {
            versionIndex: 'number';
            entityName: 'string';
            projectName: 'string';
            collectionName: 'string';
          };
        };
      };
      artifactVersionId: 'string';
      artifactTypeName: 'string';
      entityName: 'string';
      projectName: 'string';
      artifactName: 'string';
      versionIndex: 'number';
      versionDescription: 'string';
      numConsumers: 'number';
      numFiles: 'number';
      artifactSize: 'number';
      ttlDurationSeconds: 'number';
      ttlDiffString: 'string';
      ttlIsInherited: 'boolean';
      entityArtifactTTL: 'number';
      digest: 'string';
      createdAt: 'any';
      artifactIsGenerated: 'boolean';
      artifactIsLinkedToGlobalRegistry: 'boolean';
      artifactIsPortfolio: 'any';
      portfolioMemberships: 'any';
      peerArtifacts: {
        type: 'list';
        objectType: {
          type: 'typedDict';
          propertyTypes: {
            versionName: 'string';
            type: 'string';
            entityName: 'string';
            projectName: 'string';
          };
        };
      };
    };
  }>;
}
